'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAppForm } from '@repo/form';
import { z } from '@repo/schema';
import type { SavedViewSummary } from '@repo/schema/table-configuration';
import { useQuery } from '@repo/tanstack/query';
import { Button, Label, Tags, TagsContent, TagsEmpty, TagsGroup, TagsInput, TagsItem, TagsList, TagsTrigger, TagsValue } from '@repo/ui/components';
import { CheckIcon } from '@repo/ui/icons';
import type { IconName } from '@repo/ui/icons/dynamic';
import { type ReactNode, useState } from 'react';
import { isIconName, SavedViewIconPicker } from './saved-view-icon-picker.tsx';
import { productError } from './product-error.ts';
const detailsSchema = z.object({
  description: z.string().trim().max(500, 'Use 500 characters or fewer'),
  icon: z.string().nullable(),
  name: z.string().trim().min(1, 'Name is required').max(100, 'Use 100 characters or fewer'),
  organisationIds: z.array(z.string())
});
export type ViewDetails = {
  description: null | string;
  icon: null | string;
  name: string;
  organisation_applicability: {
    organisation_ids: number[];
    type: 'selected';
  } | {
    type: 'all';
  };
};
type DetailsValues = z.input<typeof detailsSchema>;
const toDetails = (values: DetailsValues): ViewDetails => ({
  description: values.description.trim() || null,
  icon: values.icon,
  name: values.name.trim(),
  // No chosen Organisation means every Organisation.
  organisation_applicability: values.organisationIds.length ? {
    organisation_ids: values.organisationIds.map(Number),
    type: 'selected'
  } : {
    type: 'all'
  }
});
const initialValues = (view: null | SavedViewSummary): DetailsValues => ({
  description: view?.description ?? '',
  icon: view?.icon ?? null,
  name: view?.name ?? '',
  organisationIds: view?.organisation_applicability.organisations.map(org => String(org.id)) ?? []
});

/** Name, description, icon and Organisation Applicability; never the query or columns. */
export const ViewDetailsForm = t0 => {
  const $ = _c(42);
  const {
    children,
    id,
    onSubmit,
    submitLabel,
    view
  } = t0;
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== view) {
    t1 = initialValues(view);
    $[0] = view;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSubmit) {
    t2 = async t3 => {
      const {
        value
      } = t3;
      setError(null);
      ;
      try {
        await onSubmit(toDetails(value));
      } catch (t4) {
        const failure = t4;
        setError(productError(failure instanceof Error ? failure : new Error(String(failure))));
      }
    };
    $[2] = onSubmit;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      onSubmit: detailsSchema
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2) {
    t4 = {
      defaultValues: t1,
      onSubmit: t2,
      validators: t3
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    AppField,
    AppForm,
    handleSubmit,
    Subscribe
  } = useAppForm(t4);
  let t5;
  if ($[8] !== handleSubmit) {
    t5 = event => {
      event.preventDefault();
      event.stopPropagation();
      handleSubmit();
    };
    $[8] = handleSubmit;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label htmlFor="name" data-sentry-element="Label" data-sentry-source-file="view-details-form.tsx">Name</Label>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== AppField) {
    t7 = <AppField name="icon" data-sentry-element="AppField" data-sentry-source-file="view-details-form.tsx">{_temp}</AppField>;
    $[11] = AppField;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== AppField) {
    t8 = <div className="min-w-0 flex-1"><AppField name="name" data-sentry-element="AppField" data-sentry-source-file="view-details-form.tsx">{_temp2}</AppField></div>;
    $[13] = AppField;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t6}<div className="flex items-start gap-2">{t7}{t8}</div></div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== AppField) {
    t10 = <AppField name="description" data-sentry-element="AppField" data-sentry-source-file="view-details-form.tsx">{_temp3}</AppField>;
    $[18] = AppField;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== view?.organisation_applicability.organisations) {
    t11 = field_2 => <OrganisationsField current={view?.organisation_applicability.organisations ?? []} onChange={field_2.handleChange} value={field_2.state.value} />;
    $[20] = view?.organisation_applicability.organisations;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== AppField || $[23] !== t11) {
    t12 = <AppField name="organisationIds" data-sentry-element="AppField" data-sentry-source-file="view-details-form.tsx">{t11}</AppField>;
    $[22] = AppField;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== error) {
    t13 = error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null;
    $[25] = error;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== Subscribe || $[28] !== submitLabel) {
    t14 = submitLabel ? <Subscribe selector={_temp4}>{isSubmitting => <div className="flex justify-end"><Button disabled={isSubmitting} type="submit">{submitLabel}</Button></div>}</Subscribe> : null;
    $[27] = Subscribe;
    $[28] = submitLabel;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== children || $[31] !== id || $[32] !== t10 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t5 || $[37] !== t9) {
    t15 = <form className="flex flex-col gap-4" id={id} onSubmit={t5}>{t9}{t10}{t12}{children}{t13}{t14}</form>;
    $[30] = children;
    $[31] = id;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t5;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== AppForm || $[40] !== t15) {
    t16 = <AppForm data-sentry-element="AppForm" data-sentry-component="ViewDetailsForm" data-sentry-source-file="view-details-form.tsx">{t15}</AppForm>;
    $[39] = AppForm;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
};

/**
 * The Organisations a view applies to. The list loads when it opens; until then the chips use
 * the names the view already has. Inactive Organisations are offered only while the view
 * already applies to them.
 */
const OrganisationsField = t0 => {
  const $ = _c(84);
  const {
    current,
    onChange,
    value
  } = t0;
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] !== api.organisations.list) {
    t1 = api.organisations.list.queryOptions();
    $[0] = api.organisations.list;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled: open
    };
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3
  } = useQuery(t2);
  let T0;
  let T1;
  let T2;
  let T3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== current || $[6] !== onChange || $[7] !== open || $[8] !== search || $[9] !== t3 || $[10] !== value) {
    const organisations = t3 === undefined ? [] : t3;
    const query = search.trim().toLowerCase();
    const choices = organisations.filter(org => (org.active || current.some(item => item.id === org.id)) && org.name.toLowerCase().includes(query));
    let t25;
    if ($[36] !== current) {
      t25 = org_0 => !current.some(item_0 => item_0.id === org_0.id);
      $[36] = current;
      $[37] = t25;
    } else {
      t25 = $[37];
    }
    const named = [...current, ...organisations.filter(t25)];
    const chosen = named.filter(org_1 => value.includes(String(org_1.id)));
    let t26;
    if ($[38] !== onChange || $[39] !== value) {
      t26 = id => onChange(value.includes(id) ? value.filter(item_1 => item_1 !== id) : [...value, id]);
      $[38] = onChange;
      $[39] = value;
      $[40] = t26;
    } else {
      t26 = $[40];
    }
    const toggle = t26;
    t21 = "flex flex-col gap-2";
    t22 = "OrganisationsField";
    t23 = "view-details-form.tsx";
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <Label data-sentry-element="Label" data-sentry-source-file="view-details-form.tsx">Organisations</Label>;
      $[41] = t24;
    } else {
      t24 = $[41];
    }
    T3 = Tags;
    t13 = "w-full";
    t14 = setOpen;
    t15 = open;
    t16 = setSearch;
    t17 = search;
    t18 = "Tags";
    t19 = "view-details-form.tsx";
    const t27 = chosen.length === 0 ? <span className="text-muted-foreground">Every Organisation</span> : chosen.map(org_2 => <TagsValue key={org_2.id} onRemove={() => toggle(String(org_2.id))} variant="outline">{org_2.name}</TagsValue>);
    if ($[42] !== t27) {
      t20 = <TagsTrigger aria-label="Organisations" variant="outline" data-sentry-element="TagsTrigger" data-sentry-source-file="view-details-form.tsx">{t27}</TagsTrigger>;
      $[42] = t27;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    T2 = TagsContent;
    t10 = "TagsContent";
    t11 = "view-details-form.tsx";
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <TagsInput onValueChange={setSearch} placeholder={"Search Organisations\u2026"} data-sentry-element="TagsInput" data-sentry-source-file="view-details-form.tsx" />;
      $[44] = t12;
    } else {
      t12 = $[44];
    }
    T1 = TagsList;
    t7 = "TagsList";
    t8 = "view-details-form.tsx";
    if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TagsEmpty data-sentry-element="TagsEmpty" data-sentry-source-file="view-details-form.tsx">No Organisations found.</TagsEmpty>;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    T0 = TagsGroup;
    t4 = "TagsGroup";
    t5 = "view-details-form.tsx";
    let t28;
    if ($[46] !== toggle || $[47] !== value) {
      t28 = org_3 => <TagsItem key={org_3.id} onSelect={() => toggle(String(org_3.id))} value={`${org_3.name} ${org_3.id}`}>{org_3.name}{value.includes(String(org_3.id)) ? <CheckIcon className="text-muted-foreground" size={14} /> : null}</TagsItem>;
      $[46] = toggle;
      $[47] = value;
      $[48] = t28;
    } else {
      t28 = $[48];
    }
    t6 = choices.map(t28);
    $[5] = current;
    $[6] = onChange;
    $[7] = open;
    $[8] = search;
    $[9] = t3;
    $[10] = value;
    $[11] = T0;
    $[12] = T1;
    $[13] = T2;
    $[14] = T3;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
    $[22] = t17;
    $[23] = t18;
    $[24] = t19;
    $[25] = t20;
    $[26] = t21;
    $[27] = t22;
    $[28] = t23;
    $[29] = t24;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    T2 = $[13];
    T3 = $[14];
    t10 = $[15];
    t11 = $[16];
    t12 = $[17];
    t13 = $[18];
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
    t17 = $[22];
    t18 = $[23];
    t19 = $[24];
    t20 = $[25];
    t21 = $[26];
    t22 = $[27];
    t23 = $[28];
    t24 = $[29];
    t4 = $[30];
    t5 = $[31];
    t6 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
  }
  let t25;
  if ($[49] !== T0 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6) {
    t25 = <T0 data-sentry-element={t4} data-sentry-source-file={t5}>{t6}</T0>;
    $[49] = T0;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== T1 || $[55] !== t25 || $[56] !== t7 || $[57] !== t8 || $[58] !== t9) {
    t26 = <T1 data-sentry-element={t7} data-sentry-source-file={t8}>{t9}{t25}</T1>;
    $[54] = T1;
    $[55] = t25;
    $[56] = t7;
    $[57] = t8;
    $[58] = t9;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== T2 || $[61] !== t10 || $[62] !== t11 || $[63] !== t12 || $[64] !== t26) {
    t27 = <T2 data-sentry-element={t10} data-sentry-source-file={t11}>{t12}{t26}</T2>;
    $[60] = T2;
    $[61] = t10;
    $[62] = t11;
    $[63] = t12;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== T3 || $[67] !== t13 || $[68] !== t14 || $[69] !== t15 || $[70] !== t16 || $[71] !== t17 || $[72] !== t18 || $[73] !== t19 || $[74] !== t20 || $[75] !== t27) {
    t28 = <T3 className={t13} onOpenChange={t14} open={t15} setValue={t16} value={t17} data-sentry-element={t18} data-sentry-source-file={t19}>{t20}{t27}</T3>;
    $[66] = T3;
    $[67] = t13;
    $[68] = t14;
    $[69] = t15;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t20;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <p className="text-xs text-muted-foreground">Leave empty to use this view in every Organisation.</p>;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] !== t21 || $[79] !== t22 || $[80] !== t23 || $[81] !== t24 || $[82] !== t28) {
    t30 = <div className={t21} data-sentry-component={t22} data-sentry-source-file={t23}>{t24}{t28}{t29}</div>;
    $[78] = t21;
    $[79] = t22;
    $[80] = t23;
    $[81] = t24;
    $[82] = t28;
    $[83] = t30;
  } else {
    t30 = $[83];
  }
  return t30;
};
function _temp(field) {
  return <SavedViewIconPicker onValueChange={icon => field.handleChange(icon)} value={field.state.value && isIconName(field.state.value) ? field.state.value : null} variant="icon" />;
}
function _temp2(field_0) {
  return <field_0.Input />;
}
function _temp3(field_1) {
  return <field_1.Textarea label="Description" />;
}
function _temp4(state) {
  return state.isSubmitting;
}